import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationPrefs,
} from '@/api/endpoints';
import { Card } from '@/components/ui/Card';

const PREFS_KEY = ['me', 'notification-preferences'] as const;

const ROWS: Array<{ id: NotificationCategory; label: string; hint?: string; locked?: boolean }> = [
  { id: 'monthly_fee_notice', label: 'Fee notices', hint: 'What you have paid in transaction fees.' },
  { id: 'withdrawal_updates', label: 'Withdrawal updates', hint: 'When a withdrawal is initiated or paid.' },
  { id: 'engagement', label: 'Product updates' },
  { id: 'marketing', label: 'Marketing', hint: 'Off unless you opt in.' },
  {
    id: 'security',
    label: 'Security alerts',
    hint: 'Always on — these cannot be turned off.',
    locked: true,
  },
];

const CHANNELS: NotificationChannel[] = ['email', 'push'];

/*
  The PUT takes a PARTIAL and the server merges it, so a toggle sends ONLY the
  one channel/category it changed. Sending the whole object back would let a
  stale read silently revert a change made in another tab.
*/
export function NotificationsCard() {
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: PREFS_KEY,
    queryFn: notificationsApi.get,
    staleTime: 60_000,
  });

  const patch = useMutation({
    mutationFn: (body: Partial<NotificationPrefs>) => notificationsApi.patch(body),
    onSuccess: (resp) => qc.setQueryData(PREFS_KEY, resp),
  });

  const prefs = data?.preferences;

  return (
    <Card title="Notifications">
      {isLoading && <p className="text-sm text-white/45">Loading…</p>}
      {isError && <p className="text-sm text-red-400">Could not load your preferences.</p>}

      {prefs && (
        <>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center">
            <span />
            {CHANNELS.map((c) => (
              <span
                key={c}
                className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/40 text-center w-12"
              >
                {c}
              </span>
            ))}

            {ROWS.map((row) => (
              <Row key={row.id} row={row} prefs={prefs} onToggle={(body) => patch.mutate(body)} />
            ))}
          </div>

          {patch.error && (
            <p className="mt-3 text-sm text-red-400">Could not save that change. Please try again.</p>
          )}
        </>
      )}
    </Card>
  );
}

function Row({
  row,
  prefs,
  onToggle,
}: {
  row: (typeof ROWS)[number];
  prefs: NotificationPrefs;
  onToggle: (body: Partial<NotificationPrefs>) => void;
}) {
  return (
    <>
      <div className="py-2.5 border-t border-white/[0.07]">
        <div className="text-sm">{row.label}</div>
        {row.hint && <div className="text-xs text-white/35">{row.hint}</div>}
      </div>
      {CHANNELS.map((channel) => {
        const on = row.locked ? true : (prefs[channel]?.[row.id] ?? false);
        return (
          <div key={channel} className="py-2.5 border-t border-white/[0.07] grid place-items-center w-12">
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={`${row.label} — ${channel}`}
              disabled={row.locked}
              onClick={() => onToggle({ [channel]: { [row.id]: !on } })}
              className={`w-9 h-5 rounded-full transition relative ${
                on ? 'bg-brand-teal' : 'bg-white/15'
              } ${row.locked ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'}`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                  on ? 'left-[18px]' : 'left-0.5'
                }`}
              />
            </button>
          </div>
        );
      })}
    </>
  );
}
